import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, LoaderCircle, PencilLine, Plus, Save, ShieldOff, X } from 'lucide-react';
import { useAuth } from '../../auth';
import { userHasRole } from '../../roles';
import { telephonyApi } from '../../telephony/telephonyApi';
import type {
  TelephonyBlacklistDirection,
  TelephonyBlacklistPatternType,
  TelephonyBlacklistPayload,
  TelephonyBlacklistRule,
} from '../../telephony/types';

type RuleForm = {
  pattern: string;
  patternType: TelephonyBlacklistPatternType;
  direction: TelephonyBlacklistDirection;
  reason: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
};

const EMPTY_FORM: RuleForm = {
  pattern: '',
  patternType: 'EXACT',
  direction: 'BOTH',
  reason: '',
  startsAt: '',
  endsAt: '',
  isActive: true,
};

function inputDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function displayDate(value?: string | null) {
  if (!value) return 'без ограничения';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function directionLabel(value: TelephonyBlacklistDirection) {
  if (value === 'INBOUND') return 'Только входящие';
  if (value === 'OUTBOUND') return 'Только исходящие';
  return 'Входящие и исходящие';
}

function statusLabel(rule: TelephonyBlacklistRule) {
  if (!rule.is_active) return 'Отключено';
  if (rule.active_now) return 'Применено в АТС';
  if (new Date(rule.starts_at).getTime() > Date.now()) return 'Запланировано';
  return 'Срок завершён';
}

function payloadFromForm(form: RuleForm): TelephonyBlacklistPayload {
  return {
    pattern: form.pattern.trim(),
    patternType: form.patternType,
    direction: form.direction,
    reason: form.reason.trim(),
    ...(form.startsAt ? { startsAt: new Date(form.startsAt).toISOString() } : {}),
    ...(form.endsAt ? { endsAt: new Date(form.endsAt).toISOString() } : { endsAt: null }),
    isActive: form.isActive,
  };
}

export function TelephonyBlacklistPanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RuleForm>(EMPTY_FORM);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const admin = userHasRole(user, 'ADMIN');

  const rulesQuery = useQuery({
    queryKey: ['telephony-blacklist'],
    queryFn: telephonyApi.blacklist,
    enabled: admin,
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['telephony-blacklist'] });
  const saveRule = useMutation({
    mutationFn: (payload: TelephonyBlacklistPayload) => editingId
      ? telephonyApi.updateBlacklistRule(editingId, payload)
      : telephonyApi.createBlacklistRule(payload),
    onSuccess: async () => {
      setError('');
      setMessage(editingId ? 'Правило изменено и применено в АТС' : 'Правило добавлено и применено в АТС');
      setEditingId(null);
      setForm(EMPTY_FORM);
      await refresh();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось применить правило'),
  });
  const deactivate = useMutation({
    mutationFn: telephonyApi.deactivateBlacklistRule,
    onSuccess: async () => {
      setError('');
      setMessage('Правило отключено и конфигурация АТС обновлена');
      setEditingId(null);
      setForm(EMPTY_FORM);
      await refresh();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось отключить правило'),
  });

  if (!admin) return <div className="form-error">Управление чёрным списком доступно только администратору.</div>;

  function edit(rule: TelephonyBlacklistRule) {
    setEditingId(rule.id);
    setForm({
      pattern: rule.phone_normalized,
      patternType: rule.pattern_type,
      direction: rule.direction,
      reason: rule.reason,
      startsAt: inputDate(rule.starts_at),
      endsAt: inputDate(rule.ends_at),
      isActive: rule.is_active,
    });
    setMessage('');
    setError('');
  }

  function reset() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMessage('');
    setError('');
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.pattern.trim()) return;
    const action = editingId ? 'изменить' : 'добавить';
    if (!window.confirm(`Подтвердите: ${action} правило и сразу применить новую конфигурацию FreeSWITCH?`)) return;
    setMessage('');
    setError('');
    saveRule.mutate(payloadFromForm(form));
  }

  function disable(rule: TelephonyBlacklistRule) {
    if (!window.confirm(`Отключить правило «${rule.phone_normalized}» и применить изменение в FreeSWITCH?`)) return;
    setMessage('');
    setError('');
    deactivate.mutate(rule.id);
  }

  const rules = rulesQuery.data ?? [];
  const pending = saveRule.isPending || deactivate.isPending;

  return <div className="admin-section-stack">
    {(message || error || rulesQuery.error) && <div className={`admin-feedback ${error || rulesQuery.error ? 'error' : 'success'}`}>
      {error || (rulesQuery.error instanceof Error ? rulesQuery.error.message : '') || message}
    </div>}
    <div className="management-layout wide-form">
      <section className="surface telephony-log">
        <div className="surface-head"><div><h2>Чёрный список телефонии</h2><p>Правила проверяются сервером и FreeSWITCH до маршрутизации звонка.</p></div><button className="button ghost compact" type="button" onClick={reset}><Plus /> Новое правило</button></div>
        <div className="telephony-call-head"><span>Номер или префикс</span><span>Совпадение</span><span>Период</span><span>Состояние</span><span>Изменил</span><span>Действия</span></div>
        {rulesQuery.isLoading ? <div className="telephony-settings-loading"><LoaderCircle /> Загружаем правила…</div> : rules.map(rule => <div className="telephony-call-row" key={rule.id}>
          <span><b>{rule.phone_normalized}</b><small>{rule.reason || 'Причина не указана'}</small></span>
          <span><b>{rule.pattern_type === 'PREFIX' ? 'Префикс' : 'Точный номер'}</b><small>{directionLabel(rule.direction)}</small></span>
          <span><b>с {displayDate(rule.starts_at)}</b><small>до {displayDate(rule.ends_at)}</small></span>
          <span><em className={`telephony-call-status ${rule.active_now ? 'completed' : rule.is_active ? 'ringing' : 'failed'}`}>{statusLabel(rule)}</em></span>
          <span><b>{rule.updated_by_name || rule.created_by_name || 'Система'}</b><small>{displayDate(rule.updated_at)}</small></span>
          <span className="telephony-call-actions"><button className="soft-button compact" type="button" onClick={() => edit(rule)}><PencilLine /> Изменить</button>{rule.is_active && <button className="soft-button compact danger" type="button" disabled={pending} onClick={() => disable(rule)}><Ban /> Отключить</button>}</span>
        </div>)}
        {!rulesQuery.isLoading && !rules.length && <div className="telephony-settings-loading"><ShieldOff /> Правил пока нет</div>}
      </section>

      <form className="surface compact-form" onSubmit={submit}>
        <div className="editor-title"><div><h2>{editingId ? 'Изменить правило' : 'Новое правило'}</h2><p className="form-description">После подтверждения правило безопасно применяется к АТС. При ошибке изменение откатывается.</p></div>{editingId && <button className="icon-button" type="button" onClick={reset} aria-label="Закрыть редактирование"><X /></button>}</div>
        <label>Номер или цифровой префикс<input name="pattern" value={form.pattern} onChange={event => setForm(value => ({ ...value, pattern: event.target.value }))} placeholder="+7 999 123-45-67" required /></label>
        <div className="form-grid two">
          <label>Тип совпадения<select value={form.patternType} onChange={event => setForm(value => ({ ...value, patternType: event.target.value as TelephonyBlacklistPatternType }))}><option value="EXACT">Точный номер</option><option value="PREFIX">Префикс</option></select></label>
          <label>Направление<select value={form.direction} onChange={event => setForm(value => ({ ...value, direction: event.target.value as TelephonyBlacklistDirection }))}><option value="BOTH">Все звонки</option><option value="INBOUND">Только входящие</option><option value="OUTBOUND">Только исходящие</option></select></label>
        </div>
        <label>Причина<textarea rows={3} maxLength={500} value={form.reason} onChange={event => setForm(value => ({ ...value, reason: event.target.value }))} placeholder="Например, нежелательные рекламные звонки" /></label>
        <label>Начало действия<input type="datetime-local" value={form.startsAt} onChange={event => setForm(value => ({ ...value, startsAt: event.target.value }))} /></label>
        <label>Окончание действия<input type="datetime-local" value={form.endsAt} onChange={event => setForm(value => ({ ...value, endsAt: event.target.value }))} /></label>
        <label className="checkbox-row"><input type="checkbox" checked={form.isActive} onChange={event => setForm(value => ({ ...value, isActive: event.target.checked }))} /><span>Правило включено</span></label>
        <button className="button primary wide" disabled={pending || !form.pattern.trim()}><Save /> {pending ? 'Применяем…' : editingId ? 'Сохранить и применить' : 'Добавить правило'}</button>
      </form>
    </div>
  </div>;
}
